"use client";

import type { JSX, MouseEvent } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLenis } from "@/components/providers/SmoothScrollProvider";
import { scrollToHash } from "@/lib/scroll-to-hash";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const ctaContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const ctaItemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.65, 0, 0.35, 1] },
  },
};

const heroActions = [
  {
    label: "Talk to a Specialist",
    onClick: () => openWhatsAppWithUtm(WA_NUMBERS, "accounting-services"),
    className:
      "inline-flex h-14 items-center justify-center whitespace-nowrap rounded-[40px] border-0 bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] hover:bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] px-5 py-4 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm sm:text-base lg:text-[clamp(6px,1.25vw,18px)] font-normal leading-5 tracking-[0] text-white transition-colors cursor-pointer",
  },
] as const;

export const HeroSection = (): JSX.Element => {
  const lenisRef = useLenis();

  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-black px-4 pt-16 sm:px-8 sm:pt-24 lg:pt-[109px]">
      <div className="flex w-full sm:h-[780px] max-w-[1186px] flex-col items-start">
        <div className="flex w-full max-w-[950px] flex-col sm:items-start items-center  gap-2">
         
          <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100">
            HK Accounting
          </Badge>
     
          <div className="flex w-full flex-col items-center gap-11 sm:items-start">
            <div className="flex w-full flex-col items-center gap-3 sm:items-start">
                 <motion.h1
                   className="text-center text-[34px] leading-[40px] sm:text-left sm:text-[42px] sm:leading-[54px] lg:text-[56px] lg:leading-[71px] font-semibold  [font-family:'Suisse_Intl_Trial-Regular',Helvetica]"
                   variants={headingVariants}
                   initial="hidden"
                   whileInView="visible"
                   viewport={{ once: true, amount: 0.6 }}
                 >
            Hong Kong{" "}
            <span className="bg-[radial-gradient(50%_50%_at_50%_50%,#0061FE_34.13%,#DAE8FF_100%)] bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-[34px] leading-[40px] sm:text-[48px] sm:leading-[54px] lg:text-[56px] lg:leading-[71px] italic font-normal">
              Accounting <br className="hidden sm:block" /> Services
            </span>{" "}
          
            for Financial <br className="hidden sm:block" /> Compliance
          </motion.h1>
              <motion.p
                className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] w-full text-center text-lg font-normal leading-7 tracking-[0] text-[#585d70] sm:w-[65%] sm:text-left sm:text-xl"
                variants={headingVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.6 }}
              >
          Get your Hong Kong company accounts prepared by qualified accountants
without having to manage financial statements, reconciliations and year-
end accounts yourself.
              </motion.p>
            </div>
            <motion.div
              id="hero-cta-buttons"
              className="flex w-full flex-col items-center gap-[21px] sm:w-auto sm:flex-row sm:flex-wrap sm:items-start"
              variants={ctaContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              {heroActions.map((action) => (
                <motion.div
                  key={action.label}
                  className="w-[70vw] sm:w-auto"
                  variants={ctaItemVariants}
                >
                  <Button
                    asChild
                    className={`w-[70vw] justify-center sm:w-[260px] ${action.className}`}
                  >
                    <button type="button" onClick={action.onClick}>
                      {action.label}
                    </button>
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          </div>
              <div className=" mt-4 flex items-center justify-center gap-5">
              <img
                className="w-[120px] h-5"
                alt="Frame"
                src="/home-v3/frame-122-1.png"
              />
              <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] whitespace-nowrap text-sm lg:text-[clamp(6px,1.25vw,18px)] font-normal leading-4 tracking-[0] text-[#585d70]">
                1k+ Reviews 4.5/5.0 Stars
              </p>
            </div>
        </div>
      </div>
      <div className="pointer-events-none absolute left-1/2 -top-4  hidden w-full max-w-[1440px] -translate-x-1/2 lg:block">
        <img
          className="absolute right-0 w-[768px] h-[796px]"
          alt="Image"
          src="/hong-kong-accounting-services/hero.svg"
        />
        {/* <div className="absolute left-[778px] top-[103px] rounded-[20px_20px_20px_0px] border-2 border-[#ffffff1a] bg-[#ffffff0f] px-3 py-[13px] backdrop-blur-[52px]">
          <img
            className="absolute -left-[27px] top-[74px] h-[38px] w-[38px]"
            alt="Group"
            src="/hong-kong-company-formation-v2/group-127.png"
          />
          <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-[17px] font-normal leading-[23px] text-white">
            Your company has been
            <br />
            Successfully Registered
          </p>
        </div> */}
      </div>
      {/* <div className="relative mx-auto flex max-w-[1066px] flex-col items-center gap-8 px-5 pb-10 pt-16 bg-black sm:pt-24 lg:pt-32">
              <motion.h2
                className="text-[22px] text-center leading-[140%] sm:text-[24px] sm:leading-[140%] lg:text-[32px] lg:leading-[140%] font-semibold  [font-family:'Suisse_Intl_Trial-Regular',Helvetica]"
                variants={headingVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.6 }}
              >
            Hong Kong{" "}
            <span className="bg-[radial-gradient(50%_50%_at_50%_50%,#0061FE_34.13%,#DAE8FF_100%)] bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-[22px] leading-[140%] sm:text-[24px] sm:leading-[140%] lg:text-[32px] lg:leading-[140%] italic font-normal">
              Company Formation Services
            </span>{" "}
            with <br className="hidden sm:block" /> Banking and Payment Set Up
          </motion.h2>
        <motion.p
          className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-center text-[17px] font-normal leading-6 text-[#fefefe]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Priority business account opening local with our partners
        </motion.p>
        <motion.img
          className="w-full"
          alt="Container"
          src="/hong-kong-company-formation-v2/container-1.svg"
          variants={ctaItemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        />
      </div> */}
    </section>
  );
};
